import { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  Space,
  Empty,
  Typography,
  Button,
  Spin,
  message,
  Tag,
  Divider,
  Tooltip,
} from "antd";
import {
  CheckSquareOutlined,
  BorderOutlined,
  EnvironmentOutlined,
  DownloadOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useNavigate } from "react-router";

import { apiClient } from "../../../services/api/apiClient";

/**
 * Mapa global del productor: pinta las PlotReal canónicas de TODAS sus
 * fincas activas, coloreadas por variedad. Permite togglear capas
 * por finca (chips inferior) y por variedad (chips superior). Misma
 * estética que FarmMapCard / AuditMapCard (ArcGIS satélite, popup,
 * highlight al click, KML/PNG export).
 */

interface PlotForMap {
  id: number;
  name: string | null;
  variety_id: number | null;
  variety_name: string | null;
  variety_code: string | null;
  surface: number | null;
  surface_gps: number | null;
  rootstock_name: string | null;
  geom_geojson: GeoJSON.Geometry | null;
}

interface FarmForMap {
  id: number;
  name: string;
  town_name: string | null;
  province_name: string | null;
  plot_count: number;
  plots: PlotForMap[];
}

const xmlSafe = (s: string | null | undefined): string =>
  (s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

interface Props {
  producerId: number;
  producerName?: string | null;
}

// Resaltamos TANGO en naranja (la variedad foco del negocio) y damos
// a las otras variedades colores de paleta saturada — todos
// suficientemente distintos entre sí y del fondo satélite.
const VARIETY_PALETTE = [
  "#722ed1", // morado
  "#13c2c2", // teal
  "#eb2f96", // magenta
  "#52c41a", // verde
  "#1677ff", // azul
  "#fadb14", // amarillo
  "#a0522d", // marrón
];
const COLOR_TANGO = "#fa8c16";
const COLOR_NO_VARIETY = "#bfbfbf";

const isTango = (p: PlotForMap): boolean => {
  const code = (p.variety_code || "").toUpperCase();
  const name = (p.variety_name || "").toUpperCase();
  return code === "TANGO" || name.includes("TANGO");
};

const fmtNum = (v: number | null | undefined, dec = 2) =>
  v != null
    ? Number(v).toLocaleString("es-ES", {
        minimumFractionDigits: dec,
        maximumFractionDigits: dec,
      })
    : "—";

const buildPopup = (p: PlotForMap, farm: FarmForMap) => {
  const locationLine =
    farm.town_name && farm.province_name
      ? `${xmlSafe(farm.town_name)} (${xmlSafe(farm.province_name)})`
      : xmlSafe(farm.town_name || farm.province_name || "");
  return `
    <div style="min-width:220px;font-size:12px;">
      <div style="font-weight:600;font-size:13px;margin-bottom:2px;">
        ${xmlSafe(p.name || `#${p.id}`)}
      </div>
      ${p.variety_name ? `<div style="margin-bottom:6px;">Variedad: <strong>${xmlSafe(p.variety_name)}</strong></div>` : "<div style=\"margin-bottom:6px;color:#bfbfbf;\">Sin variedad</div>"}
      ${p.surface != null ? `<div>Sup. auditada: ${fmtNum(p.surface)} ha</div>` : ""}
      ${p.surface_gps != null ? `<div>Sup. GPS: ${fmtNum(p.surface_gps)} ha</div>` : ""}
      ${p.rootstock_name ? `<div>Patrón: ${xmlSafe(p.rootstock_name)}</div>` : ""}
      <div style="border-top:1px solid #f0f0f0;margin:8px 0 6px;padding-top:6px;">
        <div style="color:#8c8c8c;font-size:11px;letter-spacing:0.3px;margin-bottom:2px;">FINCA</div>
        <div style="font-weight:600;">${xmlSafe(farm.name)}</div>
        ${locationLine ? `<div style="color:#8c8c8c;">${locationLine}</div>` : ""}
        <div style="color:#8c8c8c;font-size:11px;margin-top:2px;">${farm.plot_count} parcelas canónicas</div>
      </div>
      <button
        type="button"
        data-farm-link
        data-farm-id="${farm.id}"
        style="display:inline-flex;align-items:center;gap:4px;color:#1677ff;text-decoration:none;font-weight:500;background:none;border:none;padding:0;cursor:pointer;font-size:12px;"
      >
        Ver detalle de la finca →
      </button>
    </div>
  `;
};

/* ------------------------------------------------------------------ */

const Chip: React.FC<{
  label: string;
  color: string;
  count: number;
  on: boolean;
  dashed?: boolean;
  onClick: () => void;
}> = ({ label, color, count, on, dashed, onClick }) => (
  <span
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick();
      }
    }}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "3px 10px",
      border: "1px solid",
      borderColor: on ? "#d9d9d9" : "#f0f0f0",
      borderRadius: 14,
      background: on ? "#fafafa" : "transparent",
      cursor: "pointer",
      userSelect: "none",
      fontSize: 12,
      color: on ? "#262626" : "#bfbfbf",
      textDecoration: on ? "none" : "line-through",
      opacity: on ? 1 : 0.6,
      transition: "opacity 0.15s, background 0.15s",
    }}
  >
    <span
      style={{
        display: "inline-block",
        width: 14,
        height: 14,
        borderRadius: 3,
        background: color + "44", // ~27% alpha
        border: `2px ${dashed ? "dashed" : "solid"} ${color}`,
      }}
    />
    <span>{label}</span>
    <span
      style={{
        background: on ? "#fff" : "#fafafa",
        border: "1px solid",
        borderColor: on ? "#d9d9d9" : "#e8e8e8",
        color: on ? "#595959" : "#bfbfbf",
        padding: "0 6px",
        borderRadius: 10,
        fontSize: 11,
        fontWeight: 600,
        minWidth: 18,
        textAlign: "center",
      }}
    >
      {count}
    </span>
  </span>
);

/* ------------------------------------------------------------------ */

export const ProducerMapCard: React.FC<Props> = ({
  producerId,
  producerName,
}) => {
  const navigate = useNavigate();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<L.Map | null>(null);
  const polysRef = useRef<
    Map<
      string,
      {
        layer: L.GeoJSON;
        farmId: number;
        varietyKey: string;
        baseStyle: L.PathOptions;
      }
    >
  >(new Map());
  const highlightedRef = useRef<string | null>(null);

  const [data, setData] = useState<{ farms: FarmForMap[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [backfilling, setBackfilling] = useState(false);
  // Bumpear este contador re-dispara el fetch del endpoint farms-map
  // tras un backfill exitoso.
  const [reloadKey, setReloadKey] = useState(0);

  // Visibilidad por finca y por variedad (sets de keys habilitados).
  const [visibleFarms, setVisibleFarms] = useState<Set<number>>(new Set());
  const [visibleVarieties, setVisibleVarieties] = useState<Set<string>>(
    new Set(),
  );

  /* ---- Fetch ----------------------------------------------------- */

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await apiClient.get<{ farms: FarmForMap[] }>(
          `/v1/producers/${producerId}/farms-map/`,
        );
        if (!cancelled) {
          setData(res.data);
          // Por defecto, todas las fincas y todas las variedades visibles.
          setVisibleFarms(new Set(res.data.farms.map((f) => f.id)));
          const allVarieties = new Set<string>();
          res.data.farms.forEach((f) =>
            f.plots.forEach((p) => allVarieties.add(varietyKey(p))),
          );
          setVisibleVarieties(allVarieties);
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(
            e?.response?.data?.detail ||
              e?.message ||
              "No se pudo cargar el mapa del productor.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [producerId, reloadKey]);

  const handleBackfill = async () => {
    setBackfilling(true);
    try {
      const res = await apiClient.post<{
        audits_processed: number;
        audits_skipped: number;
        revisions_created: number;
        errors: { audit_id: number; error: string }[];
      }>(`/v1/producers/${producerId}/backfill-canonical/`);
      const r = res.data;
      if (r.revisions_created === 0 && r.audits_processed === 0) {
        message.info(
          "No hay auditorías en estados extra para rellenar canónicas.",
        );
      } else {
        message.success(
          `Procesadas ${r.audits_processed} auditorías, ${r.revisions_created} revisiones nuevas.`,
        );
      }
      if (r.errors.length > 0) {
        // eslint-disable-next-line no-console
        console.warn("[ProducerMapCard] backfill errors", r.errors);
      }
      // Forzar recarga del mapa con la nueva geometría.
      setReloadKey((k) => k + 1);
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || "Error";
      message.error(detail);
    } finally {
      setBackfilling(false);
    }
  };

  /* ---- Derivar variedades visibles y colores --------------------- */

  // Key estable por variedad: "tango" para TANGO, "v:<id>" para resto,
  // "_none" para sin variedad. Permite mapear chip→plot consistentemente.
  const varietyKey = (p: PlotForMap): string => {
    if (isTango(p)) return "tango";
    if (p.variety_id != null) return `v:${p.variety_id}`;
    return "_none";
  };

  const varietySummary = useMemo(() => {
    if (!data) return [] as { key: string; label: string; color: string; count: number; dashed?: boolean }[];
    const map = new Map<
      string,
      { key: string; label: string; color: string; count: number; dashed?: boolean }
    >();
    let paletteIdx = 0;
    const codeToColor = new Map<string, string>();

    for (const f of data.farms) {
      for (const p of f.plots) {
        const k = varietyKey(p);
        if (!map.has(k)) {
          let color = COLOR_TANGO;
          let label = p.variety_name || "Sin variedad";
          let dashed = false;
          if (k === "_none") {
            color = COLOR_NO_VARIETY;
            label = "Sin variedad";
            dashed = true;
          } else if (k !== "tango") {
            const codeKey = p.variety_code || String(p.variety_id);
            if (!codeToColor.has(codeKey)) {
              codeToColor.set(
                codeKey,
                VARIETY_PALETTE[paletteIdx % VARIETY_PALETTE.length],
              );
              paletteIdx += 1;
            }
            color = codeToColor.get(codeKey)!;
            label = p.variety_name || `Variedad #${p.variety_id}`;
          }
          map.set(k, { key: k, label, color, count: 0, dashed });
        }
        map.get(k)!.count += 1;
      }
    }
    // Tango primero, sin variedad al final.
    const arr = Array.from(map.values());
    arr.sort((a, b) => {
      if (a.key === "tango") return -1;
      if (b.key === "tango") return 1;
      if (a.key === "_none") return 1;
      if (b.key === "_none") return -1;
      return a.label.localeCompare(b.label);
    });
    return arr;
  }, [data]);

  const colorByVarietyKey = useMemo(() => {
    const m = new Map<string, { color: string; dashed?: boolean }>();
    varietySummary.forEach((v) =>
      m.set(v.key, { color: v.color, dashed: v.dashed }),
    );
    return m;
  }, [varietySummary]);

  /* ---- Styles ---------------------------------------------------- */

  const styleFor = (vk: string): L.PathOptions => {
    const meta = colorByVarietyKey.get(vk);
    const color = meta?.color || COLOR_NO_VARIETY;
    return {
      color,
      weight: 2,
      fillColor: color,
      fillOpacity: vk === "tango" ? 0.35 : 0.22,
      dashArray: meta?.dashed ? "5 4" : undefined,
    };
  };

  const highlightStyleFor = (vk: string): L.PathOptions => {
    const base = styleFor(vk);
    return {
      ...base,
      weight: 4,
      fillOpacity: 0.55,
      dashArray: undefined,
    };
  };

  const clearHighlight = () => {
    const cur = highlightedRef.current;
    if (!cur) return;
    const entry = polysRef.current.get(cur);
    if (entry) entry.layer.setStyle(entry.baseStyle);
    highlightedRef.current = null;
  };

  /* ---- Init map -------------------------------------------------- */

  // Calcular hasAny aquí (no abajo) para usarlo como dep del init.
  // Si el div del mapa solo se renderiza cuando hay datos, el effect
  // tiene que disparar cuando hasAny pasa de false→true para coger
  // el ref recién montado.
  const hasAnyForInit =
    !!data && data.farms.some((f) => f.plots.some((p) => p.geom_geojson));

  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;
    const map = L.map(mapRef.current, {
      center: [40.02, -4.9],
      zoom: 14,
      zoomControl: true,
    });
    mapInstance.current = map;

    const arcgisSatellite = L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      { maxZoom: 19, attribution: "&copy; Esri" },
    );
    const arcgisLabels = L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
      { maxZoom: 19, attribution: "&copy; Esri" },
    );
    const googleSatellite = L.tileLayer(
      "https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}",
      { maxZoom: 20, attribution: "&copy; Google" },
    );
    const osm = L.tileLayer(
      "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      { maxZoom: 19, attribution: "&copy; OpenStreetMap" },
    );
    arcgisSatellite.addTo(map);
    L.control
      .layers(
        {
          "Satélite (ArcGIS)": arcgisSatellite,
          "Satélite + etiquetas": L.layerGroup([arcgisSatellite, arcgisLabels]),
          "Satélite (Google)": googleSatellite,
          OpenStreetMap: osm,
        },
        undefined,
        { position: "topright", collapsed: true },
      )
      .addTo(map);

    map.on("click", () => clearHighlight());

    // Event delegation a nivel del contenedor del mapa: cualquier
    // click sobre un elemento con [data-farm-link] dispara la
    // navegación, independientemente de cuándo Leaflet montó el popup
    // o si recicla el DOM al reabrir. Es la forma más robusta de
    // garantizar el navigate con state.
    const container = map.getContainer();
    const onContainerClick = (ev: MouseEvent) => {
      const target = ev.target as HTMLElement | null;
      const btn = target?.closest?.("[data-farm-link]") as HTMLElement | null;
      if (!btn) return;
      ev.preventDefault();
      ev.stopPropagation();
      const fid = Number(btn.getAttribute("data-farm-id"));
      if (Number.isFinite(fid)) {
        navigate(`/farms/show/${fid}`, { state: { from: "producer" } });
      }
    };
    container.addEventListener("click", onContainerClick);

    return () => {
      container.removeEventListener("click", onContainerClick);
      map.remove();
      mapInstance.current = null;
      polysRef.current.clear();
      highlightedRef.current = null;
    };
  }, [hasAnyForInit]);

  /* ---- Repaint polygons when data changes ------------------------ */

  useEffect(() => {
    if (!mapInstance.current || !data) return;
    const map = mapInstance.current;

    // Limpieza
    polysRef.current.forEach((entry) => {
      map.removeLayer(entry.layer);
    });
    polysRef.current.clear();
    highlightedRef.current = null;

    const allLayers: L.Layer[] = [];

    for (const farm of data.farms) {
      for (const p of farm.plots) {
        if (!p.geom_geojson) continue;
        const vk = varietyKey(p);
        const base = styleFor(vk);
        const layer = L.geoJSON(p.geom_geojson as any, { style: base });
        layer.bindPopup(buildPopup(p, farm));
        const key = `${farm.id}:${p.id}`;
        polysRef.current.set(key, {
          layer,
          farmId: farm.id,
          varietyKey: vk,
          baseStyle: base,
        });
        layer.on("click", (e) => {
          L.DomEvent.stopPropagation(e as any);
          clearHighlight();
          layer.setStyle(highlightStyleFor(vk));
          highlightedRef.current = key;
          layer.openPopup();
        });
        // El click del botón "Ver detalle de la finca" se captura por
        // delegación a nivel del contenedor del mapa (ver init effect),
        // así no dependemos del timing de popupopen.
        layer.addTo(map);
        allLayers.push(layer);
      }
    }

    if (allLayers.length > 0) {
      const fg = L.featureGroup(allLayers);
      const b = fg.getBounds();
      if (b.isValid()) {
        map.fitBounds(b, { padding: [24, 24], maxZoom: 18 });
      }
    }
  }, [data, colorByVarietyKey]);

  /* ---- Apply visibility ----------------------------------------- */

  useEffect(() => {
    if (!mapInstance.current) return;
    const map = mapInstance.current;
    polysRef.current.forEach((entry) => {
      const visible =
        visibleFarms.has(entry.farmId) &&
        visibleVarieties.has(entry.varietyKey);
      if (visible && !map.hasLayer(entry.layer)) {
        entry.layer.addTo(map);
      } else if (!visible && map.hasLayer(entry.layer)) {
        map.removeLayer(entry.layer);
      }
    });
    // Si el polígono resaltado se ha ocultado, limpiamos.
    const cur = highlightedRef.current;
    if (cur) {
      const e = polysRef.current.get(cur);
      if (
        e &&
        (!visibleFarms.has(e.farmId) || !visibleVarieties.has(e.varietyKey))
      ) {
        clearHighlight();
      }
    }
  }, [visibleFarms, visibleVarieties]);

  /* ---- KML export ------------------------------------------------ */

  const downloadKml = () => {
    if (!data) return;
    try {
      const xmlEscape = (s: string) =>
        s
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;");
      const ringToCoords = (ring: number[][]): string =>
        ring.map((pt) => `${pt[0]},${pt[1]},0`).join(" ");
      const geometryToKml = (g: GeoJSON.Geometry): string => {
        const polygonXml = (rings: number[][][]): string => {
          const outer = rings[0] || [];
          const holes = rings.slice(1);
          const holesXml = holes
            .map(
              (h) =>
                `<innerBoundaryIs><LinearRing><coordinates>${ringToCoords(h)}</coordinates></LinearRing></innerBoundaryIs>`,
            )
            .join("");
          return `<Polygon><outerBoundaryIs><LinearRing><coordinates>${ringToCoords(outer)}</coordinates></LinearRing></outerBoundaryIs>${holesXml}</Polygon>`;
        };
        if (g.type === "Polygon")
          return polygonXml(g.coordinates as number[][][]);
        if (g.type === "MultiPolygon")
          return `<MultiGeometry>${(g.coordinates as number[][][][]).map(polygonXml).join("")}</MultiGeometry>`;
        return "";
      };
      // Colores KML AABBGGRR. Reusamos la paleta visible.
      const hexToKmlBgr = (hex: string, alphaHex: string): string => {
        const h = hex.replace("#", "");
        const r = h.slice(0, 2);
        const g = h.slice(2, 4);
        const b = h.slice(4, 6);
        return `${alphaHex}${b}${g}${r}`;
      };

      const styles = varietySummary
        .map(
          (v) =>
            `<Style id="${v.key}"><LineStyle><color>${hexToKmlBgr(v.color, "ff")}</color><width>2</width></LineStyle><PolyStyle><color>${hexToKmlBgr(v.color, "66")}</color><fill>1</fill><outline>1</outline></PolyStyle></Style>`,
        )
        .join("");

      const farmFolders = data.farms
        .map((f) => {
          const placemarks = f.plots
            .filter((p) => p.geom_geojson)
            .map((p) => {
              const vk = varietyKey(p);
              const desc = [
                p.variety_name ? `Variedad: ${p.variety_name}` : "Sin variedad",
                p.surface != null ? `Sup. auditada: ${fmtNum(p.surface)} ha` : "",
                p.surface_gps != null ? `Sup. GPS: ${fmtNum(p.surface_gps)} ha` : "",
              ]
                .filter(Boolean)
                .join("\n");
              return `<Placemark><name>${xmlEscape(p.name || `#${p.id}`)}</name><description>${xmlEscape(desc)}</description><styleUrl>#${vk}</styleUrl>${geometryToKml(p.geom_geojson!)}</Placemark>`;
            })
            .join("");
          return `<Folder><name>${xmlEscape(f.name)}</name><description>${f.plot_count} parcelas</description>${placemarks}</Folder>`;
        })
        .join("");

      const slug = (producerName || "productor")
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/[^a-zA-Z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .slice(0, 60) || "productor";
      const random = Math.floor(100000 + Math.random() * 900000);
      const baseName = `${slug}_productor_${random}`;
      const docName = xmlEscape(baseName);

      const kml = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${docName}</name>
    <description>${xmlEscape((producerName || "Productor") + ` — ${data.farms.length} fincas`)}</description>
    ${styles}
    ${farmFolders}
  </Document>
</kml>`;

      const blob = new Blob([kml], {
        type: "application/vnd.google-earth.kml+xml",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${baseName}.kml`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      message.success(`KML descargado: ${baseName}.kml`);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[ProducerMapCard] KML failed", e);
      message.error("No se pudo generar el KML");
    }
  };

  /* ---- Helpers ---------------------------------------------------- */

  const toggleFarm = (farmId: number) =>
    setVisibleFarms((s) => {
      const next = new Set(s);
      next.has(farmId) ? next.delete(farmId) : next.add(farmId);
      return next;
    });
  const toggleVariety = (key: string) =>
    setVisibleVarieties((s) => {
      const next = new Set(s);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  const setAllFarms = (on: boolean) => {
    if (!data) return;
    setVisibleFarms(on ? new Set(data.farms.map((f) => f.id)) : new Set());
  };

  /* ---- Render ---------------------------------------------------- */

  // Reusa el flag que el init effect usa como dep para que no haya
  // dos fuentes de verdad.
  const hasAny = hasAnyForInit;

  // invalidateSize cuando se monta el card (por si está dentro de un
  // contenedor que ajusta dimensión).
  useEffect(() => {
    if (!mapInstance.current) return;
    const t = setTimeout(() => mapInstance.current?.invalidateSize(), 100);
    return () => clearTimeout(t);
  }, [hasAny]);

  // Observador de tamaño del contenedor — el mapa puede vivir en una
  // columna responsive (xs full → lg 12/24). Cuando el ancho cambia,
  // Leaflet necesita invalidateSize() para repintar tiles bien.
  useEffect(() => {
    const el = mapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      // Pequeño debounce — varias resizes seguidas durante el reflow.
      window.requestAnimationFrame(() => {
        mapInstance.current?.invalidateSize();
      });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <Card
      size="small"
      title={
        <Space>
          <EnvironmentOutlined />
          <strong>Mapa del productor</strong>
          {data && (
            <Tag color="blue">{data.farms.length} fincas</Tag>
          )}
        </Space>
      }
      extra={
        <Space size={4}>
          {/* Re-materializa canónicas incluyendo audits en estados
              extra (Realizada con Informe, Checkeada, Realizada sin
              Informe). Útil cuando alguna finca tiene PRs sin geom
              pero hay AuditPlots históricas con geom. */}
          <Tooltip title="Rellena canónicas desde auditorías en estados 'Realizada con Informe', 'Checkeada' y 'Realizada sin Informe'. Útil cuando una finca tiene parcelas sin geometría pero existen auditorías históricas con datos.">
            <Button
              size="small"
              icon={<ThunderboltOutlined />}
              onClick={handleBackfill}
              loading={backfilling}
            >
              Rellenar canónicas
            </Button>
          </Tooltip>
          {hasAny && (
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={downloadKml}
            >
              KML
            </Button>
          )}
        </Space>
      }
      bordered={false}
      style={{ marginBottom: 16 }}
      bodyStyle={{ padding: hasAny ? 0 : 16 }}
    >
      {loading && (
        <div style={{ padding: 48, textAlign: "center" }}>
          <Spin size="large" />
        </div>
      )}
      {error && !loading && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={error} />
      )}
      {!loading && !error && hasAny && (
        <>
          {/* Leyenda superior: variedades */}
          <div
            style={{
              padding: "10px 12px",
              background: "#fafafa",
              borderBottom: "1px solid #f0f0f0",
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              alignItems: "center",
            }}
          >
            <Typography.Text
              type="secondary"
              style={{ fontSize: 11, marginRight: 4 }}
            >
              VARIEDAD:
            </Typography.Text>
            {varietySummary.map((v) => (
              <Chip
                key={v.key}
                label={v.label}
                color={v.color}
                count={v.count}
                dashed={v.dashed}
                on={visibleVarieties.has(v.key)}
                onClick={() => toggleVariety(v.key)}
              />
            ))}
          </div>

          {/* Mapa */}
          <div
            ref={mapRef}
            style={{ height: 540, width: "100%", borderRadius: 0 }}
          />

          {/* Leyenda inferior: fincas */}
          <div
            style={{
              padding: "10px 12px",
              borderTop: "1px solid #f0f0f0",
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              alignItems: "center",
            }}
          >
            <Typography.Text
              type="secondary"
              style={{ fontSize: 11, marginRight: 4 }}
            >
              FINCAS:
            </Typography.Text>
            <Button
              type="link"
              size="small"
              icon={<CheckSquareOutlined />}
              onClick={() => setAllFarms(true)}
              style={{ padding: "0 4px", fontSize: 11 }}
            >
              Todas
            </Button>
            <Button
              type="link"
              size="small"
              icon={<BorderOutlined />}
              onClick={() => setAllFarms(false)}
              style={{ padding: "0 4px", fontSize: 11 }}
            >
              Ninguna
            </Button>
            <Divider type="vertical" style={{ margin: "0 4px" }} />
            {data!.farms.map((f) => {
              const on = visibleFarms.has(f.id);
              return (
                <span
                  key={f.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleFarm(f.id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      toggleFarm(f.id);
                    }
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "3px 10px",
                    border: "1px solid",
                    borderColor: on ? "#d9d9d9" : "#f0f0f0",
                    borderRadius: 14,
                    background: on ? "#fafafa" : "transparent",
                    cursor: "pointer",
                    userSelect: "none",
                    fontSize: 12,
                    color: on ? "#262626" : "#bfbfbf",
                    textDecoration: on ? "none" : "line-through",
                    opacity: on ? 1 : 0.6,
                  }}
                  title={
                    f.town_name && f.province_name
                      ? `${f.town_name} (${f.province_name})`
                      : undefined
                  }
                >
                  <span>{f.name}</span>
                  <span
                    style={{
                      background: on ? "#fff" : "#fafafa",
                      border: "1px solid",
                      borderColor: on ? "#d9d9d9" : "#e8e8e8",
                      color: on ? "#595959" : "#bfbfbf",
                      padding: "0 6px",
                      borderRadius: 10,
                      fontSize: 11,
                      fontWeight: 600,
                      minWidth: 18,
                      textAlign: "center",
                    }}
                  >
                    {f.plot_count}
                  </span>
                </span>
              );
            })}
          </div>
        </>
      )}
      {!loading && !error && !hasAny && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Este productor no tiene fincas con parcelas canónicas dibujadas."
        />
      )}
    </Card>
  );
};

export default ProducerMapCard;
